import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  Chart as ChartJS,
  type ChartConfiguration,
  type Plugin,
} from "chart.js/auto";
import { getThemeClasses } from "../../utils/ThemeClasses";
import { getCustomSelectStyles } from "../../Layouts/admin/shared";
import type { AggPoint } from "../../features/dashboards/dailyHistoryAgg";

export type FlowStyle = "wave" | "bars";

const FLOW_OPTS: { value: FlowStyle; label: string }[] = [
  { value: "wave", label: "Onda" },
  { value: "bars", label: "Barras" },
];

type ThemeClasses = ReturnType<typeof getThemeClasses>;

const ACCENT = "#0d9488";
const ACCENT_SOFT = "rgba(13, 148, 136, 0.16)";
const INDIGO = "#6366f1";
const INDIGO_SOFT = "rgba(99, 102, 241, 0.16)";
const GREEN = "#22c55e";
const GREEN_SOFT = "rgba(34, 197, 94, 0.16)";
const RED = "#ef4444";
const RED_SOFT = "rgba(239, 68, 68, 0.16)";

const isDarkTheme = (themeClasses: ThemeClasses) => themeClasses.text === "text-white";

const themePalette = (dark: boolean) => {
  return {
    tick: dark ? "#cbd5e1" : "#475569",
    grid: dark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)",
    divider: dark ? "rgba(148,163,184,0.28)" : "rgba(15,23,42,0.14)",
    tooltipBg: dark ? "#0f172a" : "#ffffff",
    tooltipText: dark ? "#f1f5f9" : "#0f172a",
    tooltipBorder: dark ? "rgba(148,163,184,0.3)" : "rgba(15,23,42,0.12)",
  };
};

const nf = (v: number) => Number(v).toLocaleString("es-MX");

const monthDividerPlugin = (
  points: AggPoint[],
  color: string,
  enabled: boolean,
): Plugin => ({
  id: "monthDivider",
  afterDraw(chart) {
    if (!enabled) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    points.forEach((p, i) => {
      if (i === 0 || !p.monthStart) return;
      const x = xScale.getPixelForValue(i);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    });
    ctx.restore();
  },
});

function ChartCanvas({
  config,
  className,
}: {
  config: ChartConfiguration;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const chart = new ChartJS(canvasRef.current, config);
    chartRef.current = chart;
    return () => {
      chart.destroy();
      chartRef.current = null;
    };
  }, [config]);

  return <canvas ref={canvasRef} className={className} />;
}

function ChartShell({
  title,
  subtitle,
  titleAside,
  aggLabel,
  legend,
  controls,
  themeClasses,
  isEmpty,
  loading = false,
  children,
}: {
  title: string;
  subtitle: string;
  titleAside?: React.ReactNode;
  aggLabel?: string;
  legend?: React.ReactNode;
  controls?: React.ReactNode;
  themeClasses: ThemeClasses;
  isEmpty: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3">
        <div>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
              {title}
            </h3>
            {titleAside}
          </div>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {subtitle}
          </p>
        </div>
        <div className="flex items-end flex-wrap sm:justify-end gap-2">
          {controls}
          {legend}
          {aggLabel && (
            <span
              className={`text-[11px] whitespace-nowrap px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
            >
              {aggLabel}
            </span>
          )}
        </div>
      </div>
      <div className="h-80 sm:h-96 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <i
              className={`ri-loader-4-line animate-spin text-2xl ${themeClasses.textSecondary}`}
            />
          </div>
        ) : isEmpty ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              No hay datos para este periodo.
            </p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export function ActiveUnitsChart({
  themeClasses,
  points,
  aggLabel,
  controls,
  loading = false,
  showMonthDividers = false,
}: {
  themeClasses: ThemeClasses;
  points: AggPoint[];
  aggLabel?: string;
  controls?: React.ReactNode;
  loading?: boolean;
  showMonthDividers?: boolean;
}) {
  const isEmpty = points.length === 0;

  const isDark = isDarkTheme(themeClasses);

  const config = useMemo<ChartConfiguration>(() => {
    const palette = themePalette(isDark);
    return {
      type: "line",
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: "Unidades activas",
            data: points.map((p) => p.active),
            borderColor: ACCENT,
            backgroundColor: ACCENT_SOFT,
            fill: "origin",
            tension: 0.35,
            borderWidth: 2,
            pointRadius: points.length > 60 ? 0 : 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ACCENT,
            spanGaps: false,
          },
        ],
      },
      plugins: [monthDividerPlugin(points, palette.divider, showMonthDividers)],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            backgroundColor: palette.tooltipBg,
            titleColor: palette.tooltipText,
            bodyColor: palette.tooltipText,
            borderColor: palette.tooltipBorder,
            borderWidth: 1,
            padding: 10,
            callbacks: {
              title: (items) => points[items[0].dataIndex]?.fullLabel ?? "",
              label: (item) => {
                const p = points[item.dataIndex];
                return ` ${nf(Number(item.raw))} ${p?.days > 1 ? "prom." : "activas"}`;
              },
              afterLabel: (item) => {
                const p = points[item.dataIndex];
                return p?.days > 1 && p.activeMin != null
                  ? ` rango ${nf(p.activeMin)}–${nf(p.activeMax as number)}`
                  : "";
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: palette.tick,
              autoSkip: true,
              maxRotation: 0,
              font: { size: 10 },
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: palette.grid },
            ticks: {
              color: palette.tick,
              font: { size: 11 },
              callback: (v) => nf(Number(v)),
            },
          },
        },
      },
    };
  }, [points, isDark, showMonthDividers]);

  return (
    <ChartShell
      title="Unidades Activas"
      subtitle="Unidades con señal — nivel del día (promedio al agrupar)"
      aggLabel={aggLabel}
      controls={controls}
      themeClasses={themeClasses}
      isEmpty={isEmpty}
      loading={loading}
    >
      <ChartCanvas config={config} />
    </ChartShell>
  );
}

export function EffectivenessChart({
  themeClasses,
  points,
  title = "Efectividad",
  subtitle = "Porcentaje de unidades reportando — nivel del día (promedio al agrupar)",
  aggLabel,
  controls,
  loading = false,
  showMonthDividers = false,
}: {
  themeClasses: ThemeClasses;
  points: AggPoint[];
  title?: string;
  subtitle?: string;
  aggLabel?: string;
  controls?: React.ReactNode;
  loading?: boolean;
  showMonthDividers?: boolean;
}) {
  const isEmpty = points.length === 0;

  const isDark = isDarkTheme(themeClasses);

  const config = useMemo<ChartConfiguration>(() => {
    const palette = themePalette(isDark);
    return {
      type: "line",
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: "Efectividad",
            data: points.map((p) => p.effectiveness),
            borderColor: INDIGO,
            backgroundColor: INDIGO_SOFT,
            fill: "origin",
            tension: 0.35,
            borderWidth: 2,
            pointRadius: points.length > 60 ? 0 : 2,
            pointHoverRadius: 4,
            pointBackgroundColor: INDIGO,
            spanGaps: false,
          },
        ],
      },
      plugins: [monthDividerPlugin(points, palette.divider, showMonthDividers)],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            backgroundColor: palette.tooltipBg,
            titleColor: palette.tooltipText,
            bodyColor: palette.tooltipText,
            borderColor: palette.tooltipBorder,
            borderWidth: 1,
            padding: 10,
            bodyFont: { size: 18, weight: "bold" },
            footerFont: { size: 11, weight: "normal" },
            footerColor: palette.tick,
            callbacks: {
              title: (items) => points[items[0].dataIndex]?.fullLabel ?? "",
              label: (item) => ` ${Number(item.raw).toFixed(1)}%`,
              footer: (items) => {
                const p = points[items[0].dataIndex];
                if (!p) return "";
                if (p.days === 1) {
                  return p.reporting != null && p.total != null
                    ? `reportando ${nf(p.reporting)}/${nf(p.total)}`
                    : "";
                }
                return p.effectivenessMin != null
                  ? `rango ${p.effectivenessMin}–${p.effectivenessMax}%`
                  : "";
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: palette.tick,
              autoSkip: true,
              maxRotation: 0,
              font: { size: 10 },
            },
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: palette.grid },
            ticks: {
              color: palette.tick,
              font: { size: 11 },
              callback: (v) => `${v}%`,
            },
          },
        },
      },
    };
  }, [points, isDark, showMonthDividers]);

  const dailyBadge = (
    <span
      title="Snapshot diario del DailyHistory (registrado 1 vez al día por sg-realtime)."
      className={`text-[11px] whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
    >
      <i className="ri-calendar-line" />
      Métrica diaria
    </span>
  );

  return (
    <ChartShell
      title={title}
      subtitle={subtitle}
      titleAside={dailyBadge}
      aggLabel={aggLabel}
      controls={controls}
      themeClasses={themeClasses}
      isEmpty={isEmpty}
      loading={loading}
    >
      <ChartCanvas config={config} />
    </ChartShell>
  );
}

export function AltasBajasChart({
  themeClasses,
  points,
  title = "Altas y Bajas",
  defaultStyle = "wave",
  aggLabel,
  controls,
  loading = false,
  showMonthDividers = false,
  onPointClick,
}: {
  themeClasses: ThemeClasses;
  points: AggPoint[];
  title?: string;
  defaultStyle?: FlowStyle;
  aggLabel?: string;
  controls?: React.ReactNode;
  loading?: boolean;
  showMonthDividers?: boolean;
  onPointClick?: (point: AggPoint) => void;
}) {
  const [style, setStyle] = useState<FlowStyle>(defaultStyle);
  const selectStyles = useMemo(() => {
    const base: any = getCustomSelectStyles(isDarkTheme(themeClasses));
    const wrap =
      (key: string, extra: any) =>
      (provided: any, state: any) => ({
        ...(base[key] ? base[key](provided, state) : provided),
        ...extra,
      });
    return {
      ...base,
      control: wrap("control", { minHeight: 30, fontSize: 12 }),
      valueContainer: wrap("valueContainer", { padding: "0 6px" }),
      dropdownIndicator: wrap("dropdownIndicator", { padding: 4 }),
    } as any;
  }, [themeClasses]);
  const isEmpty = points.length === 0;

  const isDark = isDarkTheme(themeClasses);

  const config = useMemo<ChartConfiguration>(() => {
    const palette = themePalette(isDark);
    const maxAbs = Math.max(
      1,
      ...points.map((p) => Math.max(p.altas ?? 0, p.bajas ?? 0)),
    );
    const altas = points.map((p) => p.altas);
    const bajas = points.map((p) => (p.bajas == null ? null : -p.bajas));

    const commonDs =
      style === "wave"
        ? { tension: 0.4, pointRadius: 0, pointHoverRadius: 4, borderWidth: 2, fill: "origin" as const, spanGaps: false }
        : { maxBarThickness: 22, borderRadius: 3 };

    return {
      type: style === "wave" ? "line" : "bar",
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: "Altas",
            data: altas,
            borderColor: GREEN,
            backgroundColor: style === "wave" ? GREEN_SOFT : GREEN,
            ...commonDs,
          },
          {
            label: "Bajas",
            data: bajas,
            borderColor: RED,
            backgroundColor: style === "wave" ? RED_SOFT : RED,
            ...commonDs,
          },
        ],
      },
      plugins: [monthDividerPlugin(points, palette.divider, showMonthDividers)],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        onClick: (_evt, elements) => {
          if (!onPointClick || !elements.length) return;
          const p = points[elements[0].index];
          if (p) onPointClick(p);
        },
        onHover: (evt, elements) => {
          const target = evt.native?.target as HTMLElement | undefined;
          if (target) target.style.cursor = elements.length && onPointClick ? "pointer" : "default";
        },
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            backgroundColor: palette.tooltipBg,
            titleColor: palette.tooltipText,
            bodyColor: palette.tooltipText,
            borderColor: palette.tooltipBorder,
            borderWidth: 1,
            padding: 10,
            callbacks: {
              title: (items) => points[items[0].dataIndex]?.fullLabel ?? "",
              label: (item) =>
                ` ${item.dataset.label}: ${nf(Math.abs(Number(item.raw)))}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: palette.tick,
              autoSkip: true,
              maxRotation: 0,
              font: { size: 10 },
            },
          },
          y: {
            min: -maxAbs,
            max: maxAbs,
            grace: "8%",
            grid: {
              color: (ctx) =>
                ctx.tick.value === 0 ? palette.divider : palette.grid,
            },
            ticks: {
              color: palette.tick,
              font: { size: 11 },
              callback: (v) => nf(Math.abs(Number(v))),
            },
          },
        },
      },
    };
  }, [points, isDark, style, showMonthDividers, onPointClick]);

  const flowLegend = (
    <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: GREEN }} />
        <span className={`text-xs ${themeClasses.textSecondary}`}>Altas</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: RED }} />
        <span className={`text-xs ${themeClasses.textSecondary}`}>Bajas</span>
      </div>
      {aggLabel && (
        <span
          className={`text-[11px] whitespace-nowrap px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
        >
          {aggLabel}
        </span>
      )}
      {onPointClick && (
        <span
          className={`text-[11px] whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
        >
          <i className="ri-cursor-line" />
          Click en un día para ver equipos
        </span>
      )}
    </div>
  );

  const legend = (
    <div className="w-24">
      <Select
        options={FLOW_OPTS}
        value={FLOW_OPTS.find((o) => o.value === style)}
        onChange={(opt) => setStyle((opt as { value: FlowStyle }).value)}
        styles={selectStyles}
        isSearchable={false}
        menuPortalTarget={document.body}
      />
    </div>
  );

  return (
    <ChartShell
      title={title}
      subtitle="Movimiento de unidades — flujo del día (suma al agrupar)"
      titleAside={flowLegend}
      legend={legend}
      controls={controls}
      themeClasses={themeClasses}
      isEmpty={isEmpty}
      loading={loading}
    >
      <ChartCanvas config={config} />
    </ChartShell>
  );
}
